import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { formatService } from '../services/api';
import { useData } from './DataContext';

const FormatContext = createContext();

export const useFormat = () => {
  const context = useContext(FormatContext);
  if (!context) throw new Error('useFormat must be used within a FormatProvider');
  return context;
};

const EXPORT_DOWNLOAD_BASE = 'https://format.gospelwiseauthor.app/exports/';

const withAnalysisDeadline = (request, timeoutMs = 45000) => {
  let timeoutId;
  const timeout = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Manuscript analysis did not respond within 45 seconds. Retry the analysis. If it repeats, share the manuscript ID and approximate time with support.'));
    }, timeoutMs);
  });

  return Promise.race([request, timeout]).finally(() => clearTimeout(timeoutId));
};

const defaultPipelineState = () => ({
  step: 1,
  metadata: { title: '', author: '', publisher: '', language: 'en', description: '' },
  file: null,
  analysis: null,
  analysisError: null,
  structure: null,
  frontMatterContent: {},
  printConfig: { trimSize: '6x9', fontSize: '11pt' },
  exportConfig: { style_preset: 'Classic Novel', ornament_type: 'star' },
  exportResult: null,
  exportType: null,
  allExports: null,
  previewData: null,
  previewHtml: null,
  previewConfirmed: false,
});

export const FormatProvider = ({ children }) => {
  const { currentUser } = useData();
  const [manuscripts, setManuscripts] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [pipelineState, setPipelineState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [globalBackMatter, setGlobalBackMatter] = useState({});
  const saveTimeoutRef = useRef(null);
  const analysisRequestRef = useRef(0);

  useEffect(() => {
    const loadBackMatter = async () => {
      if (!currentUser?.local_id) return;
      try {
        const res = await fetch(`/api/back-matter.php?user_id=${currentUser.local_id}`);
        const data = await res.json();
        if (data.content) setGlobalBackMatter(data.content);
      } catch (err) {
        console.error('Failed to load global back matter:', err);
      }
    };
    loadBackMatter();
    fetchManuscripts();
  }, [currentUser]);

  const fetchManuscripts = async () => {
    if (!currentUser?.local_id) return;
    try {
      const response = await fetch(`/api/manuscripts.php?user_id=${currentUser.local_id}`);
      const data = await response.json();
      if (Array.isArray(data)) setManuscripts(data);
    } catch (err) {
      console.error('Failed to load manuscripts:', err);
    }
  };

  const startNewProject = () => {
    analysisRequestRef.current += 1;
    setLoading(false);
    setActiveProject({ id: 'new' });
    setPipelineState(defaultPipelineState());
  };

  const loadProject = async (project) => {
    const requestId = ++analysisRequestRef.current;
    setLoading(true);
    setActiveProject(project);
    const initialFrontMatter = project.front_matter_content || {};
    setPipelineState({
      ...defaultPipelineState(),
      step: 2,
      metadata: {
        title: project.title,
        author: project.author,
        publisher: project.publisher || '',
        language: project.language || 'en',
        description: project.description || '',
      },
      file: { name: `${project.title}.docx` },
      coverPath: project.cover_path,
      frontMatterContent: initialFrontMatter,
    });
    try {
      const analysisRes = await withAnalysisDeadline(formatService.analyzeManuscript(project.id));
      if (requestId !== analysisRequestRef.current) return;
      let targetStep = 2;
      if (project.status === 'analyzed') targetStep = 3;
      if (project.status === 'exported') targetStep = 5;
      setPipelineState(prev => ({
        ...prev,
        step: targetStep,
        analysis: { score: analysisRes.health_score, report: analysisRes.analysis_report },
        analysisError: null,
        structure: analysisRes.structure,
      }));
    } catch (err) {
      if (requestId !== analysisRequestRef.current) return;
      console.error('Failed to load project details:', err);
      setPipelineState(prev => ({
        ...prev,
        step: 2,
        analysisError: err.message || 'Could not analyze this manuscript. Retry the analysis.',
      }));
    } finally {
      if (requestId === analysisRequestRef.current) setLoading(false);
    }
  };

  const retryAnalysis = async () => {
    const manuscriptId = activeProject?.id
      || pipelineState?.manuscriptId
      || pipelineState?.existingManuscriptId;

    if (!manuscriptId || String(manuscriptId) === 'new') {
      setPipelineState(prev => ({
        ...prev,
        analysisError: 'The manuscript has not been saved yet. Return to Upload and save it before retrying analysis.',
      }));
      return;
    }

    const requestId = ++analysisRequestRef.current;
    setLoading(true);
    setPipelineState(prev => ({ ...prev, analysisError: null }));

    try {
      const analysisRes = await withAnalysisDeadline(formatService.analyzeManuscript(manuscriptId));
      if (requestId !== analysisRequestRef.current) return;
      setPipelineState(prev => ({
        ...prev,
        analysis: { score: analysisRes.health_score, report: analysisRes.analysis_report },
        analysisError: null,
        structure: analysisRes.structure,
      }));
    } catch (err) {
      if (requestId !== analysisRequestRef.current) return;
      console.error('Manuscript analysis retry failed:', err);
      setPipelineState(prev => ({
        ...prev,
        analysisError: err.message || 'Could not analyze this manuscript. Retry the analysis.',
      }));
    } finally {
      if (requestId === analysisRequestRef.current) setLoading(false);
    }
  };

  const reloadProject = (project) => {
    analysisRequestRef.current += 1;
    setLoading(false);
    setActiveProject(project);
    setPipelineState({
      ...defaultPipelineState(),
      step: 1,
      metadata: {
        title: project.title,
        author: project.author,
        publisher: project.publisher || '',
        language: project.language || 'en',
        description: project.description || '',
      },
      existingManuscriptId: project.id,
      coverPath: project.cover_path,
      frontMatterContent: project.front_matter_content || {},
    });
  };

  const closeProject = () => {
    analysisRequestRef.current += 1;
    setLoading(false);
    setActiveProject(null);
    setPipelineState(null);
  };

  const goToPreviousStep = () => {
    setPipelineState(prev => {
      if (!prev || prev.step <= 1) return prev;
      if (prev.step === 4.5) return { ...prev, step: 5, exportResult: null, exportType: null, previewData: null };
      return { ...prev, step: prev.step - 1, exportResult: null };
    });
  };

  const persistFrontMatter = (content) => {
    if (!activeProject?.id || activeProject.id === 'new') return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      const formData = new FormData();
      formData.append('manuscript_id', activeProject.id);
      formData.append('title', pipelineState.metadata.title);
      formData.append('author', pipelineState.metadata.author);
      formData.append('publisher', pipelineState.metadata.publisher);
      formData.append('language', pipelineState.metadata.language);
      formData.append('description', pipelineState.metadata.description);
      formData.append('front_matter_json', JSON.stringify(content));
      try {
        await formatService.updateManuscript(formData);
      } catch (err) {
        console.error('Front matter persistence failed', err);
      }
    }, 1500);
  };

  const updateFrontMatterContent = (sectionId, fieldKey, value) => {
    setPipelineState(prev => {
      const newContent = {
        ...prev.frontMatterContent,
        [sectionId]: { ...(prev.frontMatterContent?.[sectionId] || {}), [fieldKey]: value },
      };
      persistFrontMatter(newContent);
      return { ...prev, frontMatterContent: newContent };
    });
  };

  const updateBackMatterContent = (sectionId, key, value) => {
    setPipelineState(prev => ({
      ...prev,
      backMatterContent: {
        ...(prev.backMatterContent || {}),
        [sectionId]: { ...((prev.backMatterContent || {})[sectionId] || {}), [key]: value },
      },
    }));
  };

  const updatePrintConfig = (key, value) => {
    setPipelineState(prev => ({ ...prev, printConfig: { ...prev.printConfig, [key]: value } }));
  };

  const uploadAndAnalyze = async (formData) => {
    if (!currentUser?.local_id) {
      alert('Your account is not ready to upload manuscripts. Please reload and try again.');
      return;
    }

    const requestId = ++analysisRequestRef.current;
    let savedManuscriptId = null;
    setLoading(true);
    try {
      formData.append('user_id', currentUser.local_id);
      const uploadRes = await formatService.uploadManuscript(formData);
      const manuscriptId = uploadRes?.manuscript?.id;
      savedManuscriptId = manuscriptId;

      if (!uploadRes?.success || !manuscriptId || String(manuscriptId) === 'new') {
        throw new Error(uploadRes?.error || 'The upload did not return a manuscript ID.');
      }

      const savedManuscript = {
        ...uploadRes.manuscript,
        id: manuscriptId,
      };

      // Publish the persistent ID into state before starting any analysis request.
      setActiveProject(savedManuscript);
      setPipelineState(prev => ({
        ...prev,
        step: 2,
        manuscriptId,
        existingManuscriptId: manuscriptId,
        analysis: null,
        analysisError: null,
        structure: null,
      }));

      const analysisRes = await withAnalysisDeadline(formatService.analyzeManuscript(manuscriptId));
      if (requestId !== analysisRequestRef.current) return;
      setPipelineState(prev => ({
        ...prev,
        step: 2,
        analysis: { score: analysisRes.health_score, report: analysisRes.analysis_report },
        analysisError: null,
        structure: analysisRes.structure,
      }));
      await fetchManuscripts();
    } catch (err) {
      if (requestId !== analysisRequestRef.current) return;
      console.error('Upload/Analysis failed:', err);
      if (savedManuscriptId && String(savedManuscriptId) !== 'new') {
        setPipelineState(prev => ({
          ...prev,
          step: 2,
          manuscriptId: savedManuscriptId,
          existingManuscriptId: savedManuscriptId,
          analysis: null,
          structure: null,
          analysisError: err.message || 'Analysis did not finish. Retry the analysis.',
        }));
      } else {
        setPipelineState(prev => ({
          ...prev,
          step: 1,
          analysis: null,
          structure: null,
          analysisError: err.message || 'Upload failed. Check the selected document and retry.',
        }));
        alert(`Error: ${err.message}`);
      }
    } finally {
      if (requestId === analysisRequestRef.current) setLoading(false);
    }
  };

  const updateManuscript = async (formData) => {
    setLoading(true);
    try {
      const res = await formatService.updateManuscript(formData);
      if (res.success) {
        await fetchManuscripts();
      } else {
        throw new Error(res.error || 'Update failed');
      }
    } catch (err) {
      console.error('Update failed:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const autoFixIssues = async () => {
    if (!activeProject?.id || activeProject.id === 'new') return;
    setLoading(true);
    try {
      const fixRes = await formatService.fixManuscript(activeProject.id);
      setPipelineState(prev => ({
        ...prev,
        analysis: {
          ...prev.analysis,
          score: fixRes.new_health_score,
          report: { ...prev.analysis.report, issues: {}, warnings: [] },
        },
      }));
    } catch (err) {
      console.error('Auto-fix failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const proceedToStructure = () => setPipelineState(prev => ({ ...prev, step: 3 }));
  const proceedToPrintConfig = () => setPipelineState(prev => ({ ...prev, step: 4 }));
  const proceedToExport = () => setPipelineState(prev => ({ ...prev, step: 5 }));

  const updateStructure = (section, id, included) => {
    setPipelineState(prev => {
      const newStructure = { ...prev.structure };
      const idx = newStructure[section].findIndex(item => item.id === id);
      if (idx >= 0) newStructure[section][idx].included = included;
      return { ...prev, structure: newStructure };
    });
  };

  const getPayloadForBuild = async () => {
    let freshBackMatter = globalBackMatter;
    try {
      const bmRes = await fetch(`/api/back-matter.php?user_id=${currentUser.local_id}`);
      const bmData = await bmRes.json();
      if (bmData.content) {
        freshBackMatter = bmData.content;
        setGlobalBackMatter(bmData.content);
      }
    } catch (e) {
      console.warn('Could not refresh back matter, using cached:', e);
    }
    return {
      front_matter: pipelineState.structure.front_matter.reduce((acc, item) => {
        acc[item.id] = item.included !== false; return acc;
      }, {}),
      back_matter: pipelineState.structure.back_matter.reduce((acc, item) => {
        acc[item.id] = item.included !== false; return acc;
      }, {}),
      front_matter_content: pipelineState.frontMatterContent || {},
      back_matter_content: freshBackMatter,
      book_title: pipelineState.metadata.title,
    };
  };

  // ── Build EPUB only ──────────────────────────────────────────────────────
  const buildEpub = async (stylePreset, ornamentType = 'star') => {
    if (!activeProject?.id || activeProject.id === 'new') return;
    setLoading(true);
    try {
      const payload = await getPayloadForBuild();
      const res = await formatService.buildEpub(activeProject.id, {
        ...payload, style_preset: stylePreset, ornament_type: ornamentType,
      });
      if (res.download_url && !res.download_url.includes('format.gospelwiseauthor.app')) {
        const filename = res.download_url.split('/').pop();
        res.download_url = EXPORT_DOWNLOAD_BASE + 'epub/' + filename;
      }
      const epubHtml = res.body_html || pipelineState?.previewHtml || '';
      setPipelineState(prev => ({
        ...prev,
        step: 4.5,
        exportResult: res,
        exportType: 'epub',
        previewConfirmed: false,
        previewHtml: epubHtml,
        previewData: { type: 'epub', epubHtml, pdfBase64: null },
      }));
      setManuscripts(prev => prev.map(m =>
        String(m.id) === String(activeProject.id) ? { ...m, status: 'exported' } : m
      ));
    } catch (err) {
      console.error('EPUB build failed:', err);
      alert('Failed to build EPUB: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Build PDF only ───────────────────────────────────────────────────────
  const buildPdf = async (stylePreset, ornamentType = 'star') => {
    if (!activeProject?.id || activeProject.id === 'new') return;
    setLoading(true);
    try {
      const payload = await getPayloadForBuild();
      const res = await formatService.buildPdf(activeProject.id, {
        ...payload, style_preset: stylePreset, ornament_type: ornamentType,
        print_config: pipelineState.printConfig,
      });
      if (res.download_url && !res.download_url.includes('format.gospelwiseauthor.app')) {
        const filename = res.download_url.split('/').pop();
        res.download_url = EXPORT_DOWNLOAD_BASE + 'pdf/' + filename;
      }
      const pdfBase64 = res.pdf_base64 || null;
      setPipelineState(prev => ({
        ...prev,
        step: 4.5,
        exportResult: res,
        exportType: 'pdf',
        previewConfirmed: false,
        previewData: { type: 'pdf', epubHtml: null, pdfBase64 },
      }));
      setManuscripts(prev => prev.map(m =>
        String(m.id) === String(activeProject.id) ? { ...m, status: 'exported' } : m
      ));
    } catch (err) {
      console.error('PDF build failed:', err);
      alert('Failed to build PDF: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Build BOTH formats simultaneously ────────────────────────────────────
  const buildBothFormats = async (stylePreset, ornamentType = 'star') => {
    if (!activeProject?.id || activeProject.id === 'new') return;
    setLoading(true);
    try {
      const payload = await getPayloadForBuild();

      const epubPromise = formatService.buildEpub(activeProject.id, {
        ...payload, style_preset: stylePreset, ornament_type: ornamentType,
      });
      const pdfPromise = formatService.buildPdf(activeProject.id, {
        ...payload, style_preset: stylePreset, ornament_type: ornamentType,
        print_config: pipelineState.printConfig,
      });

      // Run in parallel — if one fails, we still get the other
      const [epubRes, pdfRes] = await Promise.allSettled([epubPromise, pdfPromise]);

      const epub = epubRes.status === 'fulfilled' ? epubRes.value : null;
      const pdf = pdfRes.status === 'fulfilled' ? pdfRes.value : null;

      if (epub?.download_url && !epub.download_url.includes('format.gospelwiseauthor.app')) {
        epub.download_url = EXPORT_DOWNLOAD_BASE + 'epub/' + epub.download_url.split('/').pop();
      }
      if (pdf?.download_url && !pdf.download_url.includes('format.gospelwiseauthor.app')) {
        pdf.download_url = EXPORT_DOWNLOAD_BASE + 'pdf/' + pdf.download_url.split('/').pop();
      }

      const epubHtml = epub?.body_html || '';
      const pdfBase64 = pdf?.pdf_base64 || null;

      if (!epub && !pdf) {
        throw new Error('Both EPUB and PDF generation failed. Check server logs.');
      }

      setPipelineState(prev => ({
        ...prev,
        step: 5,
        previewConfirmed: true,
        allExports: { epub, pdf, epubHtml, pdfBase64 },
        exportResult: epub || pdf,
        exportType: epub ? 'epub' : 'pdf',
      }));

      setManuscripts(prev => prev.map(m =>
        String(m.id) === String(activeProject.id) ? { ...m, status: 'exported' } : m
      ));

      if (epubRes.status === 'rejected') {
        console.warn('EPUB failed:', epubRes.reason);
      }
      if (pdfRes.status === 'rejected') {
        console.warn('PDF failed:', pdfRes.reason);
      }
    } catch (err) {
      console.error('Dual format build failed:', err);
      alert('Failed to generate formats: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteManuscript = async (manuscriptId) => {
    setLoading(true);
    try {
      const res = await formatService.deleteManuscript(manuscriptId);
      if (res.success) {
        setManuscripts(prev => prev.filter(m => String(m.id) !== String(manuscriptId)));
        if (String(activeProject?.id) === String(manuscriptId)) closeProject();
      } else {
        throw new Error(res.error || 'Deletion failed');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete manuscript: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormatContext.Provider value={{
      manuscripts, activeProject, pipelineState, loading, globalBackMatter,
      startNewProject, loadProject, reloadProject, closeProject, goToPreviousStep,
      uploadAndAnalyze, updateManuscript, autoFixIssues, retryAnalysis,
      proceedToStructure, updateStructure, proceedToPrintConfig, updatePrintConfig,
      proceedToExport, updateFrontMatterContent, updateBackMatterContent,
      buildEpub, buildPdf, buildBothFormats,
      setPipelineState, deleteManuscript, fetchManuscripts,
    }}>
      {children}
    </FormatContext.Provider>
  );
};